import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  ContactFragment,
  CreateFinancialDealDocument,
  FinanceValueObjectsDealStageEnum,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import MoneyField from 'modules/core/components/MoneyField'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { FinanceContactsSelector } from 'modules/finance/components/Contacts/FinanceContactsSelector'
import CurrencySelector from 'modules/finance/components/CurrencySelector'
import { dealTranslateStatus, getDealStageOptions } from 'modules/finance/components/Deals/utils'

interface DealCreateModalFormValues {
  client: ContactFragment | null
  name: string
  estimatedAmount: string
  currency: string
  stage: FinanceValueObjectsDealStageEnum
  owner: number | null
  expectedCloseDate: string
}

const isValidDealStage = (val: string): val is FinanceValueObjectsDealStageEnum => {
  return [
    'new',
    'qualification',
    'proposal_sent',
    'negotiation',
    'closed_won',
    'closed_lost',
  ].includes(val)
}

export const DealCreateModal = ({
  isOpen,
  onClose,
  refetchQuery,
  defaultClient,
  stage,
  onSuccess,
}: {
  isOpen: boolean
  onClose: () => void
  refetchQuery?: string
  defaultClient?: ContactFragment | null
  stage?: FinanceValueObjectsDealStageEnum
  onSuccess?: () => void
}) => {
  const { company, employee: currentEmployee } = useCurrentSession()

  const [createDeal] = useMutation(CreateFinancialDealDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceDeal,
    refetchQueries: refetchQuery ? [refetchQuery] : undefined,
  })

  const form = useForm<DealCreateModalFormValues>({
    defaultValues: {
      client: defaultClient ?? null,
      name: '',
      estimatedAmount: '',
      currency: company.currency ?? '',
      stage: stage ?? 'new',
      owner: currentEmployee.id,
      expectedCloseDate: '',
    },
    validateOnChange: false,
    validateOnBlur: false,
    validate: (data) => {
      const errors: FormErrors<DealCreateModalFormValues> = {}

      if (!data.name?.trim()) {
        errors.name = i18n.t('common.validations.empty')
      }

      if (!data.client) {
        errors.client = i18n.t('common.validations.empty')
      }

      if (!data.estimatedAmount?.trim()) {
        errors.estimatedAmount = i18n.t('common.validations.empty')
      }

      if (!data.currency?.trim()) {
        errors.currency = i18n.t('common.validations.empty')
      }

      if (!data.stage) {
        errors.stage = i18n.t('common.validations.empty')
      }

      if (!data.owner) {
        errors.owner = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (values) => {
      const result = await createDeal({
        variables: {
          name: values.name,
          clientId: values.client?.id ?? 0,
          estimatedAmountCents: toCents(Number(values.estimatedAmount)),
          currency: values.currency,
          stage: values.stage,
          ownerId: values.owner ?? 0,
          expectedCloseDate:
            values.expectedCloseDate && values.expectedCloseDate.trim() !== ''
              ? values.expectedCloseDate
              : undefined,
        },
      })

      const handled = handleResult(result, (parsedErrors) => {
        if (parsedErrors.name || parsedErrors.pattern || parsedErrors.startFrom) {
          form.setErrors({
            name: parsedErrors.name,
            client: parsedErrors.client,
            estimatedAmount: parsedErrors.estimatedAmountCents,
            currency: parsedErrors.currency,
            stage: parsedErrors.stage,
            owner: parsedErrors.owner,
            expectedCloseDate: parsedErrors.expectedCloseDate,
          })
        } else {
          app.setFlash({
            type: 'wrong',
            message: parsedErrors.errors ?? i18n.t('errors.generic_error'),
          })
        }
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.deals.form.create_success', {
            stage: dealTranslateStatus(form.values.stage),
          }),
          time: 2000,
        })
        form.resetForm()
        onClose()
        onSuccess?.()
        return { success: true, data: values }
      } else {
        return {
          errors: [],
          success: false,
        }
      }
    },
  })

  useEffect(() => {
    if (defaultClient?.id && form.values.client?.id !== defaultClient?.id) {
      form.setFieldValue('client', defaultClient, false)
    }
  }, [defaultClient?.id])

  useEffect(() => {
    if (!isOpen || stage == null) return
    if (form.values.stage !== stage) {
      form.setFieldValue?.('stage', stage)
    }
  }, [isOpen, stage])

  const handleClose = () => {
    form.resetForm()
    onClose()
  }

  if (!isOpen) return null

  return (
    <Modal
      illustration="money-bag"
      title={i18n.t('finance.deals.form.create_title')}
      onSubmit={() => form.handleSubmit()}
      onClose={handleClose}
      footer={<Button submit>{i18n.t('finance.deals.form.actions.create')}</Button>}
    >
      <Box gap="s16">
        <FinanceContactsSelector
          value={form.values.client}
          onSelect={(value) => form.setValues({ ...form.values, client: value ?? null })}
          label={i18n.t('finance.deals.form.input_labels.client')}
          type="finance_contact"
          queryParams={{
            companyId: company.id,
            contactCreateType: 'client',
          }}
          error={form.errors.client}
          disableCreate
          mandatory
          hideSearchIcon
        />

        <TextField
          label={i18n.t('finance.deals.form.input_labels.name')}
          value={form.values.name}
          error={form.errors.name}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          placeholder={i18n.t('finance.deals.form.input_labels.name_placeholder')}
          mandatory
        />

        <Box flexDirection="row" gap="s24" alignItems="flexStart">
          <MoneyField
            label={i18n.t('finance.deals.form.input_labels.estimated_amount')}
            maxDecimals={2}
            step={1}
            currency={form.values.currency}
            value={form.values.estimatedAmount}
            error={form.errors.estimatedAmount}
            onChange={(value) => form.setValues({ ...form.values, estimatedAmount: value })}
            placeholder="0"
            mandatory
          />

          <CurrencySelector
            label={i18n.t('finance.deals.form.input_labels.currency')}
            value={form.values.currency}
            error={form.errors.currency}
            onChange={(value) => form.setValues({ ...form.values, currency: value })}
            mandatory
          />
        </Box>

        <Select
          label={i18n.t('finance.deals.form.input_labels.stage')}
          value={form.values.stage}
          onChange={(value) => {
            if (isValidDealStage(value)) {
              form.setValues({
                ...form.values,
                stage: value,
              })
            }
          }}
          options={getDealStageOptions()}
          error={form.errors.stage}
          mandatory
        />

        <EmployeeSelectorV2
          label={i18n.t('finance.deals.form.input_labels.owner')}
          value={form.values.owner ?? undefined}
          error={form.errors.owner}
          onChange={(selectedEmployeeId) =>
            form.setValues({ ...form.values, owner: selectedEmployeeId ?? null })
          }
          options={{ hideSearchIcon: true }}
          mandatory
        />

        <DayPicker
          label={i18n.t('finance.deals.form.input_labels.expected_close_date')}
          error={form.errors.expectedCloseDate}
          value={
            form.values.expectedCloseDate ? dateBackendToUI(form.values.expectedCloseDate) : ''
          }
          onChange={(value) =>
            form.setValues({
              ...form.values,
              expectedCloseDate: value && value.trim() !== '' ? dateUIToBackend(value) : '',
            })
          }
          minDate={moment()}
        />
      </Box>
    </Modal>
  )
}
